import React, { useEffect, useMemo, useState } from 'react';

import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Title as ChartTitle,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { PageSection, StyledCard, Subtitle } from 'common/styles/page';
import Select from 'react-select';

import OutcomesRetentionBarChart from './OutcomesRetentionBarChart';

import { useApiPortalV1AssessmentsResultsListQuery } from 'areas/assessment-schedule/api';
import {
  getAssessmentPeriods,
  getMostRecentClosedAssessmentPeriodId,
  getSelectedAssessmentPeriodId,
} from 'common/selectors/auth';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { AggregateType } from 'common/constants/AggregateType';
ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend);

const OutcomesRetentionPredictions = () => {
  type SelectOption = { value: string; label: string };

  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [predictionData, setPredictionData] = useState<{ label: string; value: number }[]>([]);

  const [selectedOutcome, setSelectedOutcome] = useState<string | null>(null);
  const [selectedOutcomeName, setSelectedOutcomeName] = useState<string | null>(null);

  const selectedAssessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const mostRecentClosedAssessmentPeriodId = useSelector(getMostRecentClosedAssessmentPeriodId);
  const assessmentPeriodId = selectedAssessmentPeriodId ?? mostRecentClosedAssessmentPeriodId;
  const assessmentPeriods = useSelector(getAssessmentPeriods) as any[];

  const { data: prediction_data } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.OUTCOME_PREDICTIONS,
    assessmentPeriodId: assessmentPeriodId,
  });

  const contextAssessmentPeriod = Array.isArray(assessmentPeriods)
    ? assessmentPeriods.find((period: any) => period?.id?.toString?.() === assessmentPeriodId?.toString?.())
    : undefined;

  const availableOutcomes = useMemo<SelectOption[]>(
    () =>
      Array.isArray(contextAssessmentPeriod?.outcomes)
        ? Array.from(
            new Map<string, SelectOption>(
              contextAssessmentPeriod.outcomes
                .map((outcome: any) => ({
                  value: outcome?.id?.toString?.() || '',
                  label: outcome?.name || '',
                }))
                .filter((outcome: SelectOption) => outcome.value && outcome.label)
                .map((outcome: SelectOption): [string, SelectOption] => [outcome.value, outcome])
            ).values()
          ).sort((a, b) => a.label.localeCompare(b.label))
        : [],
    [contextAssessmentPeriod]
  );
  const hasOutcomes = availableOutcomes.length > 0;

  useEffect(() => {
    if (availableOutcomes.length === 0) {
      setSelectedOutcome(null);
      setSelectedOutcomeName(null);
      return;
    }

    const selectedMatch = availableOutcomes.find(option => option.value === selectedOutcome);
    if (selectedMatch) {
      if (selectedOutcomeName !== selectedMatch.label) {
        setSelectedOutcomeName(selectedMatch.label);
      }
      return;
    }

    setSelectedOutcome(availableOutcomes[0].value);
    setSelectedOutcomeName(availableOutcomes[0].label);
  }, [availableOutcomes, selectedOutcome, selectedOutcomeName]);

  useEffect(() => {
    if (prediction_data && prediction_data.length > 0 && selectedOutcome) {
      const outcomes = prediction_data[0].result.outcomes;
      const matchedOutcome = outcomes.find(
        (outcome: { outcome_id: string }) => outcome.outcome_id === selectedOutcome
      );

      if (matchedOutcome && matchedOutcome.predictions) {
        const sortedPredictionData = [...matchedOutcome.predictions].sort(
          (a, b) => b.correlation - a.correlation
        );

        const mappedPredictionData = sortedPredictionData.map(
          (item: { pillar_name: string; correlation: number }) => ({
            label: item.pillar_name,
            value: parseFloat(item.correlation.toFixed(2)),
          })
        );

        setPredictionData(mappedPredictionData);
        return;
      }
    }

    setPredictionData(prev => (prev.length > 0 ? [] : prev));
  }, [prediction_data, selectedOutcome]);

  return (
    <>
      <PageSection display="flex" gap="10px" alignItems="flex-start">
        <StyledCard>
          <PageSection padtop="5px" padright="10px" padbottom>
            <PageSection flexDirection="column" padbottom padtop padleft>
              <PageSection padbottom display="flex" justifyContent="space-between">
                <Subtitle>{hasOutcomes ? `Predictions for ${selectedOutcomeName}` : 'Predictions'}</Subtitle>
                {hasOutcomes && (
                  <Select
                    options={availableOutcomes}
                    placeholder="Select Outcome"
                    value={availableOutcomes.find(option => option.value === selectedOutcome)}
                    onChange={option => {
                      setSelectedOutcome(option ? option.value : null);
                      setSelectedOutcomeName(option ? option.label : null);
                    }}
                    styles={{
                      container: base => ({
                        ...base,
                        width: '250px',
                      }),
                    }}
                  />
                )}
              </PageSection>
            </PageSection>
            <PageSection padleft padright>
              {!hasOutcomes ? (
                <NoDataContainer>
                  <NoDataMessage>No outcomes data is available for the selected assessment period.</NoDataMessage>
                </NoDataContainer>
              ) : predictionData && predictionData.length > 0 ? (
                <OutcomesRetentionBarChart
                  data={predictionData}
                  multiple={100}
                  percent={false}
                  fontSize="12px"
                  barHeight="20px"
                  barPadding="10px"
                  selectedIndex={selectedOption}
                  setSelectedIndex={setSelectedOption}
                />
              ) : null}
            </PageSection>
          </PageSection>
        </StyledCard>
      </PageSection>
    </>
  );
};

export default OutcomesRetentionPredictions;

const NoDataContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 280px;
  padding: 0 24px;
  box-sizing: border-box;
`;

const NoDataMessage = styled.div`
  text-align: center;
  width: 100%;
  max-width: 360px;
  white-space: normal;
  overflow-wrap: anywhere;
`;

